import Header from "../../standartComponents/header";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { pollSubscriptionStatus } from "../../../lib/subscriptionStatusApi";

import css from "./paymentStatus.module.css";

const STEPS = [
  { id: "payment",    label: "Платіж отримано банком" },
  { id: "webhook",    label: "Очікуємо підтвердження провайдера" },
  { id: "activation", label: "Активація тарифного плану" },
];

const STEP_ANIMATION_DELAY_MS = 1800;
/** After the last step, keep all steps marked "done" briefly before switching to success */
const FINAL_STEP_COMPLETE_HOLD_MS = 380;
/** While waiting for backend (not status 2), freeze here: 2nd step spins, bar stays put */
const PROGRESS_WAITING_WEBHOOK = 49;
/** After confirmation: 2/3 bar, then 3rd step animates */
const PROGRESS_AFTER_CONFIRM = 67;

const PaymentStatus = ({ authUser }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [isSuccess, setIsSuccess]   = useState(false);
  const [progress,  setProgress]    = useState(0);
  const [showDots,  setShowDots]    = useState(false);
  const [planName, setPlanName] = useState("ваш");
  const [errorMessage, setErrorMessage] = useState("");
  const tickRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    let isMounted = true;
    let pollIteration = 0;
    let waitingOnWebhook = false;

    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    /** All step indices 0..n-1 show "done" when stepIndex === STEPS.length */
    const holdAllStepsCompleteThenSuccess = async () => {
      if (!isMounted) return false;
      setStepIndex(STEPS.length);
      setProgress(100);
      await wait(FINAL_STEP_COMPLETE_HOLD_MS);
      return isMounted;
    };

    /** Full 3-step run (only when status 2 on first poll) */
    const animateStepsToSuccess = async () => {
      for (let step = 0; step < STEPS.length; step += 1) {
        if (!isMounted) return false;
        setStepIndex(step);
        setProgress(Math.round((step / STEPS.length) * 100));
        await wait(STEP_ANIMATION_DELAY_MS);
      }
      return holdAllStepsCompleteThenSuccess();
    };

    /** First step completes, then 2nd step spins at fixed % until status 2 */
    const runIntroThenWaitOnWebhook = async () => {
      if (!isMounted || waitingOnWebhook) return;
      setStepIndex(0);
      setProgress(0);
      await wait(STEP_ANIMATION_DELAY_MS);
      if (!isMounted) return;
      setStepIndex(1);
      setProgress(PROGRESS_WAITING_WEBHOOK);
      waitingOnWebhook = true;
    };

    /** User already waited on step 2; finish 2 → 3 → hold → success */
    const finishAfterWaiting = async () => {
      if (!isMounted) return false;
      setProgress(PROGRESS_AFTER_CONFIRM);
      setStepIndex(2);
      await wait(STEP_ANIMATION_DELAY_MS);
      if (!isMounted) return false;
      return holdAllStepsCompleteThenSuccess();
    };

    const runStatusLoop = async () => {
      while (isMounted) {
        const result = await pollSubscriptionStatus(1, 0);

        if (!isMounted) return;

        if (result && typeof result === "object" && result.product?.name) {
          setPlanName(result.product.name);
        }

        if (Number(result?.status) === 2) {
          if (pollIteration === 0) {
            const ok = await animateStepsToSuccess();
            if (!ok || !isMounted) return;
          } else {
            const ok = await finishAfterWaiting();
            if (!ok || !isMounted) return;
          }
          setIsSuccess(true);
          setStepIndex(STEPS.length - 1);
          setProgress(100);
          setShowDots(true);
          tickRef.current = setTimeout(() => setShowDots(false), 1500);
          return;
        }

        if (!waitingOnWebhook) {
          await runIntroThenWaitOnWebhook();
        }

        setErrorMessage("");
        pollIteration += 1;

        await wait(4000);
      }
    };

    runStatusLoop().catch((error) => {
      console.error("Payment status polling failed:", error);
      if (isMounted) {
        setErrorMessage("Не вдалося перевірити статус оплати. Оновіть сторінку, будь ласка.");
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(tickRef.current);
    };
  }, []);
 
  const getStepState = (i) => {
    if (isSuccess || i < stepIndex) return "done";
    if (i === stepIndex) return "active";
    return "pending";
  };
 
  const confettiDots = Array.from({ length: 18 }, (_, i) => ({
    id:       i,
    color:    ["#a78bfa", "#7c3aed", "#c4b5fd", "#8b5cf6", "#ddd6fe"][i % 5],
    size:     6 + Math.random() * 8,
    left:     `${Math.random() * 100}%`,
    delay:    `${Math.random() * 0.6}s`,
    duration: `${0.8 + Math.random() * 0.6}s`,
  }));

  return (
    <div style={{ width: "100%", height: "auto", overflowX: "hidden" }}>
      <Header authUser={authUser} />
      
      <div className={css.page}>
        <div className={`${css.card} ${isSuccess ? css.cardSuccess : ""}`}>
 
          {/* Confetti */}
          {showDots && (
            <div className={css.decoDots}>
              {confettiDots.map((d) => (
                <div
                  key={d.id}
                  className={css.decoDot}
                  style={{
                    width:             d.size,
                    height:            d.size,
                    background:        d.color,
                    left:              d.left,
                    bottom:            "20%",
                    animationDelay:    d.delay,
                    animationDuration: d.duration,
                  }}
                />
              ))}
            </div>
          )}
 
          {!isSuccess ? (
            /* PENDING STATE */
            <div className={css.pendingSection}>
              <div className={css.orbWrapper}>
                <div className={`${css.orbRing} ${css.orbRing1}`} />
                <div className={`${css.orbRing} ${css.orbRing2}`} />
                <div className={`${css.orbRing} ${css.orbRing3}`} />
                <div className={css.orbCenter}>💜</div>
              </div>
 
              <div className={css.statusLabel}>
                <span className={css.statusLabelDot} />
                {t('Processing payment')}
              </div>
 
              <h1 className={css.cardTitle}>
                {t('We are waiting for confirmation from the payment provider...')}
              </h1>
             {/* <p className={css.cardSubtitle}>
                Ваша оплата надійшла до банку. Як тільки ми отримаємо підтвердження,
                тарифний план буде активовано автоматично — це займає від кількох секунд
                до кількох хвилин.
              </p>*/} 
 
              <div className={css.planBadge}>
                <span className={css.planBadgeIcon}>⭐</span>
                <span className={css.planBadgeText}>{t('Your subscription plan')} «{planName}»</span>
              </div>
 
              <div className={css.progressBlock}>
                <div className={css.progressHeader}>
                  <span className={css.progressLabel}>{t('Activation progress')}</span>
                  <span className={css.progressPct}>{progress}%</span>
                </div>
                <div className={css.progressTrack}>
                  <div className={css.progressFill} style={{ width: `${progress}%` }} />
                </div>
              </div>
 
              <div className={css.steps}>
                {STEPS.map((step, i) => {
                  const state = getStepState(i);
                  return (
                    <div
                      key={step.id}
                      className={`${css.step} ${
                        state === "done"   ? css.stepDone   :
                        state === "active" ? css.stepActive : ""
                      }`}
                    >
                      <div className={`${css.stepIcon} ${
                        state === "done"   ? css.stepIconDone   :
                        state === "active" ? css.stepIconActive :
                                             css.stepIconPending
                      }`}>
                        {state === "done" ? "✓" : state === "active" ? "↻" : "○"}
                      </div>
                      <span className={`${css.stepText} ${
                        state === "done"   ? css.stepTextDone   :
                        state === "active" ? css.stepTextActive : ""
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
 
              <div className={css.footerNote}>
                🔒 {t('Do not close this page - we will notify you immediately')}
              </div>
              {errorMessage && (
                <div className={css.footerNote} style={{ marginTop: 12 }}>
                  {errorMessage}
                </div>
              )}
            </div>
 
          ) : (
            /* SUCCESS STATE */
            <div className={css.successSection}>
              <div className={css.successIconWrapper}>
                <div className={css.successRing} />
                <div className={`${css.successRing} ${css.successRing2}`} />
                <div className={css.successCircle}>🎉</div>
              </div>
 
              <h1 className={css.successTitle}>
                {t('Your subscription plan')} {" "}
                <span className={css.successPlanName}>«{planName}»</span>{" "}
                {t('Successfully activated!')}
              </h1>
 
              <div className={css.divider} />
 
              <p className={css.successDesc}>
                {t('Do not waste time - let\'s learn together! All features of the plan')} {" "}
                <strong>{planName}</strong> {t('are already available in your account.')} 🚀
              </p>
 
              <Link className={css.ctaBtn} to="/catalog">
                Продовжити навчання
                <span className={css.ctaArrow}>→</span>
              </Link>
 
              <div className={css.footerNote} style={{ marginTop: 16 }}>
                ✨ {t('Thank you for trusting AI Lingua!')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PaymentStatus;